import React, { useState } from 'react';
import { addNotification, notificationStates } from '@components/Notification';
import { holidayTypes } from './HolidayListEditor.constants';
import { HolidaysSectionView } from './HolidaysSection.view';
import { DEMO_HOLIDAYS } from './HolidaysSection.demoData';

// Demo-only build: the holiday list lives purely in local state (seeded
// from HolidaysSection.demoData.js) — no useEffectiveHolidays/companyService,
// no Firestore round trip, and no team-context switcher (the demo profile
// isn't scoped to real teams). Add/Edit/Delete/Import all resolve
// immediately, mirroring the real persistHolidays' return shape so
// HolidayListEditor/HolidayFormModal/HolidayImportModal need no changes.
export const HolidaysSectionDemoComponent = () => {
  const [holidays, setHolidays] = useState(DEMO_HOLIDAYS);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const persistHolidays = async (nextHolidays, successMessage) => {
    setHolidays(nextHolidays);
    addNotification({
      message: successMessage,
      level: notificationStates.success,
    });
    return true;
  };

  const openAddModal = () => {
    setEditingHoliday(null);
    setIsModalOpen(true);
  };

  const openEditModal = (holiday) => {
    setEditingHoliday(holiday);
    setIsModalOpen(true);
  };

  const handleModalSubmit = async (values) => {
    const nextHolidays = editingHoliday
      ? holidays.map((holiday) =>
          holiday.date === editingHoliday.date ? values : holiday
        )
      : [...holidays, values];

    const succeeded = await persistHolidays(
      nextHolidays,
      editingHoliday ? 'Holiday updated' : 'Holiday added'
    );
    if (succeeded) setIsModalOpen(false);
  };

  const handleRemove = (date) =>
    persistHolidays(
      holidays.filter((holiday) => holiday.date !== date),
      'Holiday removed'
    );

  const handleImportRows = async ({ validRows }) => {
    if (!validRows.length) return false;

    const importedHolidays = validRows.map((row) => ({
      date: row.date,
      name: row.name.trim(),
      optional: row.type === holidayTypes.optional,
      description: (row.description || '').trim(),
    }));

    const succeeded = await persistHolidays(
      [...holidays, ...importedHolidays],
      `${importedHolidays.length} holiday${
        importedHolidays.length > 1 ? 's' : ''
      } imported`
    );
    if (succeeded) setIsImportModalOpen(false);
    return succeeded;
  };

  return (
    <HolidaysSectionView
      showContextSwitcher={false}
      isTeamContext={false}
      isPersisting={false}
      showImportButton
      holidays={holidays}
      onEdit={openEditModal}
      onRemove={handleRemove}
      onAdd={openAddModal}
      isModalOpen={isModalOpen}
      onCloseModal={() => setIsModalOpen(false)}
      onSubmitModal={handleModalSubmit}
      editingHoliday={editingHoliday}
      isImportModalOpen={isImportModalOpen}
      onOpenImportModal={() => setIsImportModalOpen(true)}
      onCloseImportModal={() => setIsImportModalOpen(false)}
      onImportRows={handleImportRows}
    />
  );
};

HolidaysSectionDemoComponent.displayName = 'HolidaysSectionDemoComponent';
